import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Maximize2, X, Download, Loader2, LineChart as LineChartIcon } from "lucide-react";
import { toast } from "sonner";
import { api, shareUpload } from "@/lib/api";
import HoverTip from "@/components/HoverTip";
import ShareMenu from "@/components/ShareMenu";
import { downloadHtmlJpg, getHtmlJpgBlob } from "@/lib/chartExport";

// Customisable history chart of the ficha (16-oct-2026, replaces «Histórico POC / POV vs
// Precio»). Rows of the ratio columns are dragged in (metricDragProps), each metric is a
// chip that toggles with a tap and is removed by dragging it out (or its ×). Data: GET
// /company/{ticker}/metric-history — reported figures, annual and TTM (see CLAUDE.md).
// One canvas to compare trends: each scale (price, amounts, multiples, %, shares) gets its
// own Y axis, up to MAX_AXES, all on the same time window; «Relativa» puts every metric
// on its own min–max range so the shapes can be compared whatever their size.

export const METRIC_MIME = "application/x-vv-metric";
const CHIP_MIME = "application/x-vv-metric-chip";
const DRAG_EVENT = "vv-metric-drag";
const STORE_KEY = "vs.metricChart.v1"; // chart 1 (the original single chart); "…v1.2" for the second
const MAX_SERIES = 6; // per chart, the price included
const MAX_AXES = 4; // distinct scales (Y axes) per chart
const storeKey = (id) => (id > 1 ? `${STORE_KEY}.${id}` : STORE_KEY);
// A chip dropped on another chart moves there: the target records the move here and the
// source chart, on its dragend, removes it.
let lastMove = null;

// Categorical palette (dataviz reference palette, validated on the app surfaces: white
// card / #1C1813 dark card). A metric keeps its slot while it is in the chart, so
// removing one never repaints the others.
export const PALETTE = {
    light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"],
    dark: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
};

// One Y axis per scale: price per share, amounts in the price's currency, multiples,
// percentages (the rule of 40's points included) and share counts.
const SCALES = [
    { id: "price", title: "Precio", units: ["price"], tick: (v) => fmtAxis(v) },
    { id: "money", title: "Importes", units: ["money"], tick: (v) => fmtBig(v) },
    { id: "x", title: "Múltiplos", units: ["x"], tick: (v) => `${fmtAxis(v)}x` },
    { id: "pct", title: "Porcentajes", units: ["pct", "pts"], tick: (v) => `${fmtAxis(v)}%` },
    { id: "shares", title: "Acciones", units: ["shares"], tick: (v) => fmtBig(v) },
];
const scaleOf = (unit) => SCALES.find((g) => g.units.includes(unit))?.id || "pct";
// The price: a fixed button of the chart, on by default (toggled, never removed).
export const PRICE_KEY = "price";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const nf1 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtAxis = (v) => (Math.abs(v) >= 1000 ? nf1.format(v / 1000) + "k" : nf1.format(v));
// Amounts and share counts: T / B (miles de millones) / M / K, as the ficha's fmtNum.
export const fmtBig = (v) => {
    const a = Math.abs(v);
    if (a >= 1e12) return nf1.format(v / 1e12) + "T";
    if (a >= 1e9) return nf1.format(v / 1e9) + "B";
    if (a >= 1e6) return nf1.format(v / 1e6) + "M";
    if (a >= 1e3) return nf1.format(v / 1e3) + "K";
    return nf1.format(v);
};
const fmtValue = (unit, v, cur) => v == null ? "—"
    : unit === "x" ? `${nf1.format(v)}x` : unit === "pts" ? `${nf1.format(v)} pts`
    : unit === "price" ? `${nf2.format(v)}${cur ? ` ${cur}` : ""}`
    : unit === "money" ? `${fmtBig(v)}${cur ? ` ${cur}` : ""}`
    : unit === "shares" ? fmtBig(v) : `${nf1.format(v)} %`;
// Plotted value: fractions become percent points so both % units share the panel.
const plotted = (unit, v) => (v == null || !Number.isFinite(v) ? null : unit === "pct" ? v * 100 : v);
const dateLabel = (d, ttm) => (ttm ? `${MONTHS[+d.slice(5, 7) - 1]}'${d.slice(2, 4)}` : d.slice(0, 4));
const dateLong = (d) => `${+d.slice(8, 10)} ${MONTHS[+d.slice(5, 7) - 1]} ${d.slice(0, 4)}`;
export const Y_AXIS_W = 46;

// Dates labelled on the time axis: as many as fit in `width` px (a label every
// `labelPx`), always including the last one — counted back from it, so the latest date
// is never the one skipped.
export function axisTicks(dates, width, labelPx, axesW = Y_AXIS_W) {
    const n = dates.length;
    if (!n) return [];
    const fit = Math.max(2, Math.floor(Math.max(0, width - axesW) / labelPx));
    const step = Math.max(1, Math.ceil((n - 1) / Math.max(1, fit - 1)));
    const out = [];
    for (let i = n - 1; i >= 0; i -= step) out.unshift(dates[i]);
    return out;
}

// Axis label: the last one is anchored at its end so it isn't cut by the chart's edge.
export function EdgeTick({ x, y, payload, last, format = (v) => v }) {
    const isLast = payload?.value === last;
    return (
        <text x={x} y={y + 10} textAnchor={isLast ? "end" : "middle"} fontSize={10} fill="#7A7A7A">
            {payload?.value != null ? format(payload.value) : ""}
        </text>
    );
}

// [ref callback, width] of an element, kept up to date (the axis labels depend on it).
export function useWidth() {
    const [el, setEl] = useState(null);
    const [w, setW] = useState(0);
    useEffect(() => {
        if (!el) return undefined;
        setW(el.getBoundingClientRect().width);
        if (typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver((entries) => setW(entries[0]?.contentRect?.width || 0));
        ro.observe(el);
        return () => ro.disconnect();
    }, [el]);
    return [setEl, w];
}

// Series computed from others of /metric-history (the formula's inputs, 17-oct-2026).
const DAY = 864e5;
// Real compound growth over the `years` ending at each date (the start: the date closest
// to that many years before, within 60 days); null when either end is ≤ 0.
export function cagrSeries(block, key, years = 4) {
    const dates = block?.dates || [];
    const v = block?.series?.[key];
    if (!v) return null;
    const t = dates.map((d) => Date.parse(d));
    return dates.map((_, i) => {
        const target = t[i] - years * 365.25 * DAY;
        let j = -1;
        let best = Infinity;
        for (let k = 0; k < i; k += 1) {
            const diff = Math.abs(t[k] - target);
            if (diff < best) { best = diff; j = k; }
        }
        if (j < 0 || best > 60 * DAY) return null;
        const a = v[j];
        const b = v[i];
        return a == null || b == null || a <= 0 || b <= 0 ? null : (b / a) ** (1 / years) - 1;
    });
}
// Market capitalisation at each date: the month's close × that date's diluted shares.
export function marketCapSeries(block) {
    const p = block?.series?.price;
    const sh = block?.series?.shares;
    if (!p || !sh) return null;
    return p.map((v, i) => (v != null && sh[i] != null ? v * sh[i] : null));
}

// Props for a draggable source row (the ficha's ratio tables).
export function metricDragProps(key) {
    return {
        draggable: true,
        onDragStart: (e) => {
            e.dataTransfer.setData(METRIC_MIME, key);
            e.dataTransfer.setData("text/plain", key);
            e.dataTransfer.effectAllowed = "copy";
            window.dispatchEvent(new CustomEvent(DRAG_EVENT, { detail: true }));
        },
        onDragEnd: () => window.dispatchEvent(new CustomEvent(DRAG_EVENT, { detail: false })),
    };
}

// The price is always among the metrics (on by default); a saved selection from before
// it existed gets it added, on, in the first free colour slot.
function withPrice(metrics) {
    if (metrics.some((m) => m.key === PRICE_KEY)) return metrics;
    const used = new Set(metrics.map((m) => m.slot));
    let slot = 0;
    while (used.has(slot)) slot += 1;
    return [{ key: PRICE_KEY, on: true, slot }, ...metrics.slice(0, MAX_SERIES - 1)];
}

// Keeps the metrics of the first MAX_AXES scales (a selection saved before the limit).
function withinAxes(metrics, byKey) {
    const scales = [];
    return metrics.filter((m) => {
        const sc = byKey[m.key] ? scaleOf(byKey[m.key].unit) : null;
        if (!sc || scales.includes(sc)) return true;
        if (scales.length >= MAX_AXES) return false;
        scales.push(sc);
        return true;
    });
}

function loadState(id, byKey) {
    try {
        const s = JSON.parse(window.localStorage.getItem(storeKey(id)) || "null");
        if (s && Array.isArray(s.metrics)) {
            return { metrics: withinAxes(withPrice(s.metrics.slice(0, MAX_SERIES)), byKey),
                     mode: s.mode === "ttm" ? "ttm" : "annual", scale: s.scale === "rel" ? "rel" : "abs" };
        }
    } catch { /* private mode, blocked storage */ }
    return { metrics: withPrice([]), mode: "annual", scale: "abs" };
}

// GET /company/{ticker}/metric-history, shared by the chart and the ficha's figures
// column: one request per company per page load.
const historyCache = new Map();
function loadHistory(ticker) {
    if (!historyCache.has(ticker)) {
        const p = api.get(`/company/${encodeURIComponent(ticker)}/metric-history`).then((r) => r.data);
        p.catch(() => historyCache.delete(ticker)); // a failure is retried next time
        historyCache.set(ticker, p);
    }
    return historyCache.get(ticker);
}

// {ticker, status: loading|ok|missing|error, data} for `ticker`.
export function useMetricHistory(ticker) {
    const [hist, setHist] = useState(null);
    useEffect(() => {
        if (!ticker) return undefined;
        let alive = true;
        setHist({ ticker, status: "loading" });
        loadHistory(ticker)
            .then((data) => { if (alive) setHist({ ticker, status: "ok", data }); })
            .catch((e) => { if (alive) setHist({ ticker, status: e?.response?.status === 404 ? "missing" : "error" }); });
        return () => { alive = false; };
    }, [ticker]);
    return hist?.ticker === ticker ? hist : null;
}

const isDarkTheme = () => typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";

export function useIsDark() {
    const [dark, setDark] = useState(isDarkTheme);
    useEffect(() => {
        const mo = new MutationObserver(() => setDark(isDarkTheme()));
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
        return () => mo.disconnect();
    }, []);
    return dark;
}

function MetricTip({ active, payload, label, series, ttm, currency }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (row?.gap) return null; // an empty date between the last figures and the projection
    // The projected point: only the series the formula projects.
    const shown = row?.proj ? series.filter((s) => row[`${s.key}~proj`] != null) : series;
    const value = (s) => row?.[s.key] ?? (row?.proj ? row[`${s.key}~proj`] : null);
    return (
        <div className="bg-[#111111] font-mono text-xs leading-relaxed px-3 py-2 border border-black shadow-md" style={{ color: "#fff" }}>
            <div className="text-[#A89E8E] mb-1">{row?.date ? (row.proj ? `Proyección del cálculo a ${dateLong(row.date)}` : `${ttm ? "12 meses a" : "Cierre fiscal"} ${dateLong(row.date)}`) : label}</div>
            {shown.map((s) => (
                <div key={s.key} className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 shrink-0" style={{ background: s.color }} />
                    <span className="truncate">{s.label}</span>
                    <span className="ml-auto pl-3">{fmtValue(s.unit, value(s), currency)}</span>
                </div>
            ))}
        </div>
    );
}

// `projection` = {date, values: {key: value}}: the formula's +2-year inputs, drawn as a
// dashed stretch from the last reported date to the target date.
export default function MetricHistoryChart({ ticker, catalog, chartId = 1, projection = null }) {
    const byKey = useMemo(() => Object.fromEntries(catalog.map((m) => [m.key, m])), [catalog]);
    const [state, setState] = useState(() => loadState(chartId, byKey));
    const { metrics, mode, scale } = state;
    const rel = scale === "rel";
    const hist = useMetricHistory(ticker); // {ticker, status: loading|ok|missing|error, data}
    const [dragging, setDragging] = useState(false); // a ratio row is being dragged somewhere
    const [over, setOver] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [jpgBusy, setJpgBusy] = useState(false);
    const exportRef = useRef(null);
    const [cardRef, cardW] = useWidth();
    const [bigRef, bigW] = useWidth();
    const dark = useIsDark();

    useEffect(() => {
        try { window.localStorage.setItem(storeKey(chartId), JSON.stringify(state)); } catch { /* ignore */ }
    }, [state, chartId]);

    useEffect(() => {
        const on = (e) => { setDragging(!!e.detail); if (!e.detail) setOver(false); };
        window.addEventListener(DRAG_EVENT, on);
        return () => window.removeEventListener(DRAG_EVENT, on);
    }, []);

    const add = useCallback((key) => {
        if (!byKey[key]) return false;
        const present = metrics.find((m) => m.key === key);
        if (!present && metrics.length >= MAX_SERIES) {
            toast.error(`Como mucho ${MAX_SERIES} métricas por gráfico (el precio incluido): saca alguna arrastrándola fuera o usa el otro gráfico.`);
            return false;
        }
        const scales = new Set(metrics.filter((m) => byKey[m.key]).map((m) => scaleOf(byKey[m.key].unit)));
        if (!present && !scales.has(scaleOf(byKey[key].unit)) && scales.size >= MAX_AXES) {
            toast.error(`Como mucho ${MAX_AXES} escalas (ejes) por gráfico: saca alguna métrica de otra escala o usa el otro gráfico.`);
            return false;
        }
        setState((s) => {
            if (s.metrics.some((m) => m.key === key)) return { ...s, metrics: s.metrics.map((m) => (m.key === key ? { ...m, on: true } : m)) };
            const used = new Set(s.metrics.map((m) => m.slot));
            let slot = 0;
            while (used.has(slot)) slot += 1;
            return { ...s, metrics: [...s.metrics, { key, on: true, slot }] };
        });
        return true;
    }, [byKey, metrics]);
    const toggle = (key) => setState((s) => ({ ...s, metrics: s.metrics.map((m) => (m.key === key ? { ...m, on: !m.on } : m)) }));
    const remove = (key) => key !== PRICE_KEY && setState((s) => ({ ...s, metrics: s.metrics.filter((m) => m.key !== key) }));
    const setMode = (m) => setState((s) => ({ ...s, mode: m }));
    const setScale = (v) => setState((s) => ({ ...s, scale: v }));

    const data = hist?.ticker === ticker && hist.status === "ok" ? hist.data : null;
    const hasTtm = !!data?.ttm?.dates?.length;
    const ttm = mode === "ttm" && hasTtm;
    const block = data ? (ttm ? data.ttm : data.annual) : null;
    const unavailable = data?.unavailable || {};
    const colors = dark ? PALETTE.dark : PALETTE.light;
    const surface = dark ? "#1C1813" : "#ffffff"; // ring around the dots (card surface)

    // Series that can be drawn now, in the order they were added.
    const series = metrics
        .filter((m) => byKey[m.key])
        .map((m) => {
            const c = byKey[m.key];
            const pv = projection?.date ? projection.values?.[m.key] : null;
            return { ...c, on: m.on, color: colors[m.slot % colors.length],
                     values: (c.derive ? c.derive(block) : block?.series?.[c.source || m.key]) || null,
                     proj: pv != null && Number.isFinite(pv) ? pv : null };
        });
    const drawn = series.filter((s) => s.on && s.values?.some((v) => v != null && Number.isFinite(v)));

    // Same time window for every drawn series (CLAUDE.md, multi-series charts): it starts
    // where they all have figures, so a long history doesn't squash the others, and always
    // reaches the latest figures — a series whose figures stop earlier (a company that
    // stopped tagging a concept: Alibaba's CapEx after 2020) just ends there, noted below,
    // instead of cutting the whole chart. If a series falls outside, the whole span.
    const { rows, trimmed, projected, stale } = (() => {
        const dates = block?.dates || [];
        if (!drawn.length || !dates.length) return { rows: [], trimmed: false, projected: false, stale: [] };
        const spans = drawn.map((s) => {
            const idx = s.values.map((v, i) => (v != null && Number.isFinite(v) ? i : -1)).filter((i) => i >= 0);
            return [idx[0], idx[idx.length - 1]];
        });
        let lo = Math.max(...spans.map((r) => r[0]));
        const hi = Math.max(...spans.map((r) => r[1]));
        let cut = true;
        if (spans.some((r) => r[1] - lo < 1)) { lo = Math.min(...spans.map((r) => r[0])); cut = false; }
        // Series without figures in the last ~4 months of the window.
        const stale = drawn.map((s, k) => ({ s, end: spans[k][1] }))
            .filter(({ end }) => Date.parse(dates[hi]) - Date.parse(dates[end]) > 120 * DAY)
            .map(({ s, end }) => ({ key: s.key, label: s.label, date: dates[end] }));
        const out = [];
        for (let i = lo; i <= hi; i += 1) {
            const row = { date: dates[i], label: dateLabel(dates[i], ttm) };
            drawn.forEach((s) => { row[s.key] = plotted(s.unit, s.values[i]); });
            out.push(row);
        }
        // The formula's +2-year inputs: one more date (the target), joined to the last
        // reported value, when the window reaches the latest figures.
        const withProj = drawn.filter((s) => s.proj != null);
        const target = projection?.date;
        let projected = false;
        if (withProj.length && target && hi === dates.length - 1 && target > dates[hi]) {
            const last = out[out.length - 1];
            const pr = { date: target, label: dateLabel(target, ttm), proj: true };
            withProj.forEach((s) => {
                if (last[s.key] == null) return;
                last[`${s.key}~proj`] = last[s.key];
                pr[`${s.key}~proj`] = plotted(s.unit, s.proj);
                projected = true;
            });
            if (projected) {
                // Empty dates in between at the window's usual spacing, so the two years
                // take the room of two years on the time axis.
                const t = out.map((r) => Date.parse(r.date));
                const gaps = t.slice(1).map((v, i) => v - t[i]).sort((a, b) => a - b);
                const step = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 365.25 * DAY;
                const end = Date.parse(target);
                const n = Math.max(0, Math.round((end - t[t.length - 1]) / step) - 1);
                for (let k = 1; k <= n; k += 1) {
                    const d = new Date(t[t.length - 1] + k * step).toISOString().slice(0, 10);
                    out.push({ date: d, label: dateLabel(d, ttm), gap: true });
                }
                out.push(pr);
            }
        }
        // «Relativa»: each metric from its minimum (0) to its maximum (100) in the window.
        drawn.forEach((s) => {
            const pk = `${s.key}~proj`;
            const vals = out.flatMap((r) => [r[s.key], r[pk]]).filter((v) => v != null);
            const min = Math.min(...vals);
            const span = Math.max(...vals) - min;
            const norm = (v) => (v == null ? null : span > 0 ? ((v - min) / span) * 100 : 50);
            out.forEach((r) => {
                r[`${s.key}~rel`] = norm(r[s.key]);
                if (r[pk] != null) r[`${pk}~rel`] = norm(r[pk]);
            });
        });
        const full = Math.min(...spans.map((r) => r[0])) < lo;
        return { rows: out, trimmed: cut && full, projected, stale };
    })();

    // Y axes: one per scale, in the order the metrics were added, alternating left/right.
    const axes = SCALES.map((g) => ({ ...g, series: drawn.filter((s) => g.units.includes(s.unit)) }))
        .filter((g) => g.series.length)
        .sort((a, b) => drawn.indexOf(a.series[0]) - drawn.indexOf(b.series[0]))
        .map((g, i) => ({ ...g, side: i % 2 ? "right" : "left" }));

    // ---- drop target ----
    const accepts = (e) => {
        const types = Array.from(e.dataTransfer?.types || []);
        return types.includes(METRIC_MIME) || types.includes(CHIP_MIME);
    };
    const dropProps = {
        onDragOver: (e) => {
            if (!accepts(e)) return;
            e.preventDefault();
            const chip = Array.from(e.dataTransfer.types).includes(CHIP_MIME);
            e.dataTransfer.dropEffect = chip ? "move" : "copy";
            if (!chip) setOver(true);
        },
        onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); },
        onDrop: (e) => {
            if (!accepts(e)) return;
            e.preventDefault();
            setOver(false);
            setDragging(false);
            const key = e.dataTransfer.getData(METRIC_MIME);
            if (key) { add(key); return; }
            // A chip from another chart: it moves here (the source removes it on dragend).
            try {
                const chip = JSON.parse(e.dataTransfer.getData(CHIP_MIME) || "null");
                if (chip?.key && chip.from !== chartId && add(chip.key)) lastMove = { key: chip.key, from: chip.from };
            } catch { /* not ours */ }
        },
    };

    const downloadJpg = async () => {
        setJpgBusy(true);
        try { await downloadHtmlJpg(exportRef.current, `metricas-${ticker}`); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setJpgBusy(false); }
    };

    const addSelect = (
        <select value="" onChange={(e) => { if (e.target.value) add(e.target.value); }}
            className="border border-black bg-white px-2 py-1 text-[11px] font-mono uppercase tracking-wide max-w-[12rem] cursor-pointer"
            aria-label="Añadir métrica al gráfico" data-testid="metric-chart-add">
            <option value="">+ Métrica</option>
            {[...new Set(catalog.map((m) => m.group))].map((g) => (
                <optgroup key={g} label={g}>
                    {catalog.filter((m) => m.group === g).map((m) => (
                        <option key={m.key} value={m.key} disabled={metrics.some((x) => x.key === m.key && x.on)}>
                            {m.label}{unavailable[m.source || m.key] ? ` (${unavailable[m.source || m.key]})` : ""}
                        </option>
                    ))}
                </optgroup>
            ))}
        </select>
    );

    const modeToggle = (
        <div className="inline-flex border border-black text-[11px] font-mono uppercase tracking-wide" role="group" aria-label="Periodicidad">
            {[["annual", "Anual"], ["ttm", "TTM"]].map(([m, lbl]) => {
                const disabled = m === "ttm" && data && !hasTtm;
                const activeBtn = (m === "ttm") === ttm;
                const btn = (
                    <button key={m} type="button" onClick={() => setMode(m)} disabled={disabled}
                        className={`px-2.5 py-1 ${activeBtn ? "bg-black text-white" : "hover:bg-[#F5E4D4]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                        data-testid={`metric-chart-mode-${m}`}>{lbl}</button>
                );
                return disabled ? <HoverTip key={m} text="Esta empresa solo publica cifras anuales: no hay 12 meses por trimestre.">{btn}</HoverTip> : btn;
            })}
        </div>
    );

    const scaleToggle = (
        <div className="inline-flex border border-black text-[11px] font-mono uppercase tracking-wide" role="group" aria-label="Escala">
            {[["abs", "Absoluta", "Cada escala con su eje y sus valores reales (hasta 4 ejes)."],
              ["rel", "Relativa", "Cada métrica de su mínimo (abajo) a su máximo (arriba) en el periodo: se comparan las tendencias aunque sus tamaños no tengan nada que ver. Los valores reales, en el recuadro al pasar el ratón."]].map(([v, lbl, tip]) => (
                <HoverTip key={v} text={tip}>
                    <button type="button" onClick={() => setScale(v)} aria-pressed={scale === v}
                        className={`px-2.5 py-1 ${scale === v ? "bg-black text-white" : "hover:bg-[#F5E4D4]"}`}
                        data-testid={`metric-chart-scale-${v}`}>{lbl}</button>
                </HoverTip>
            ))}
        </div>
    );

    const chips = series.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="metric-chart-chips">
            {series.map((s) => {
                const missing = data && !s.values?.some((v) => v != null);
                const st = stale.find((x) => x.key === s.key);
                const why = unavailable[s.source || s.key] || (missing ? (ttm ? "Sin datos de 12 meses" : "Sin datos")
                    : st ? `Sin cifras publicadas después de ${dateLong(st.date)}: la empresa dejó de publicarla con una etiqueta estándar o no la desglosa` : null);
                const fixed = s.key === PRICE_KEY; // a fixed button: toggled, never removed
                return (
                    <span key={s.key} draggable={!fixed}
                        onDragStart={fixed ? undefined : (e) => {
                            lastMove = null;
                            e.dataTransfer.setData(CHIP_MIME, JSON.stringify({ key: s.key, from: chartId }));
                            e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragEnd={fixed ? undefined : (e) => {
                            const moved = lastMove && lastMove.from === chartId && lastMove.key === s.key;
                            if (e.dataTransfer.dropEffect === "none" || moved) remove(s.key);
                            lastMove = null;
                        }}
                        className={`inline-flex items-center border text-xs ${fixed ? "" : "cursor-grab active:cursor-grabbing"} select-none ${s.on ? "border-black" : "border-black/30 text-[#7A7A7A]"}`}
                        data-testid={`metric-chip-${s.key}`}>
                        <HoverTip text={`${s.on ? "Toca para ocultarlo" : "Toca para mostrarlo"}.${fixed ? " El precio está siempre disponible en el gráfico." : " Arrastra fuera del gráfico para quitarla, o al otro gráfico para llevarla allí."}${why ? `\n\n${why}.` : ""}`}>
                            <button type="button" onClick={() => toggle(s.key)} className={`inline-flex items-center gap-1.5 pl-2 ${fixed ? "pr-2" : "pr-1.5"} py-1`} aria-pressed={s.on}>
                                <span className="inline-block w-2.5 h-2.5 shrink-0" style={s.on ? { background: s.color } : { boxShadow: `inset 0 0 0 1.5px ${s.color}` }} />
                                <span className={why ? "line-through decoration-black/40" : ""}>{s.label}</span>
                            </button>
                        </HoverTip>
                        {!fixed && (
                            <button type="button" onClick={() => remove(s.key)} className="px-1.5 py-1 text-[#7A7A7A] hover:text-black border-l border-black/10"
                                aria-label={`Quitar ${s.label}`} data-testid={`metric-chip-remove-${s.key}`}><X size={12} /></button>
                        )}
                    </span>
                );
            })}
        </div>
    );

    const renderChart = (big) => {
        if (!axes.length || !rows.length) return null;
        const width = (big ? bigW : cardW) || 300;
        // Narrow screens: at most one axis per side; the others' values stay in the tooltip.
        const shown = rel ? [] : axes.filter((a, i) => width >= 480 || i < 2);
        const dates = rows.map((r) => r.date);
        const last = dates[dates.length - 1];
        // Labels: "ago'26" (TTM) needs more room than "2026"; 300 px before the width is known.
        const ticks = axisTicks(dates, width, ttm ? 50 : 38, Math.max(1, shown.length) * Y_AXIS_W);
        const h = big ? "h-[60vh] min-h-[280px] max-h-[560px]" : "h-[300px]";
        return (
            <div className={`w-full ${h}`} data-testid="metric-chart-canvas">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows} margin={{ top: 6, right: shown.some((a) => a.side === "right") ? 0 : 8, bottom: 0, left: rel ? 14 : 0 }}>
                        <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} yAxisId={rel ? "rel" : axes[0].id} />
                        <XAxis dataKey="date" ticks={ticks} interval={0} tick={<EdgeTick last={last} format={(d) => dateLabel(d, ttm)} />} tickLine={false}
                            axisLine={{ stroke: "#7A7A7A", strokeOpacity: 0.4 }} />
                        {rel
                            ? <YAxis yAxisId="rel" domain={[-4, 104]} hide />
                            : axes.map((a) => {
                                // An axis with one metric takes its colour, so it's clear which line it measures.
                                const fill = a.series.length === 1 ? a.series[0].color : "#7A7A7A";
                                const on = shown.includes(a);
                                return (
                                    <YAxis key={a.id} yAxisId={a.id} orientation={a.side} domain={["auto", "auto"]} tickFormatter={a.tick}
                                        tick={on ? { fontSize: 10, fill } : false} width={on ? Y_AXIS_W : 0} tickLine={false} axisLine={false} />
                                );
                            })}
                        <Tooltip content={<MetricTip series={drawn} ttm={ttm} currency={data?.currency} />} cursor={{ stroke: "#7A7A7A", strokeOpacity: 0.5 }} />
                        {drawn.map((s) => (
                            <Line key={s.key} yAxisId={rel ? "rel" : scaleOf(s.unit)} type="monotone" dataKey={rel ? `${s.key}~rel` : s.key}
                                stroke={s.color} strokeWidth={2} isAnimationActive={false}
                                dot={rows.length <= 16 ? { r: 4, strokeWidth: 2, stroke: surface, fill: s.color } : false}
                                activeDot={{ r: 5, strokeWidth: 2, stroke: surface }} connectNulls={false} name={s.label} />
                        ))}
                        {projected && drawn.filter((s) => s.proj != null).map((s) => (
                            <Line key={`${s.key}~proj`} yAxisId={rel ? "rel" : scaleOf(s.unit)} type="linear" dataKey={rel ? `${s.key}~proj~rel` : `${s.key}~proj`}
                                stroke={s.color} strokeWidth={2} strokeDasharray="5 4" isAnimationActive={false} legendType="none"
                                dot={(p) => (p.payload?.proj && p.value != null
                                    ? <circle key={p.key} cx={p.cx} cy={p.cy} r={4} fill={surface} stroke={s.color} strokeWidth={2} />
                                    : <g key={p.key} />)}
                                activeDot={false} connectNulls name={`${s.label} (proyección)`} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Which axis measures what (the tick formats carry the unit; this names the scales).
    const axesNote = (big) => {
        if (!rows.length || !axes.length) return null;
        if (rel) return "Escala relativa: cada métrica de su mínimo (abajo) a su máximo (arriba) en el periodo.";
        const width = (big ? bigW : cardW) || 300;
        const narrow = width < 480;
        const side = (s) => axes.filter((a, i) => a.side === s && (!narrow || i < 2)).map((a) => a.title).join(", ");
        const hidden = narrow ? axes.slice(2).map((a) => a.title) : [];
        return `Ejes · izq.: ${side("left")}${axes.length > 1 ? ` · der.: ${side("right")}` : ""}${hidden.length ? ` · sin eje (pantalla estrecha): ${hidden.join(", ")}` : ""}`;
    };

    let body;
    if (!series.length) {
        body = (
            <div className={`border border-dashed ${over ? "border-black bg-[#F5E4D4]" : "border-black/40"} flex flex-col items-center justify-center text-center gap-2 px-4 py-10 text-sm text-[#4A4A4A]`}
                data-testid="metric-chart-empty">
                <span>Arrastra aquí cualquier métrica de las columnas de la ficha o un componente de <strong>Inputs y proyecciones</strong> para ver su evolución.</span>
                <span className="text-xs">En el móvil, añádela con «+ Métrica».</span>
            </div>
        );
    } else if (!hist || hist.status === "loading" || hist.ticker !== ticker) {
        body = <div className="h-[200px] flex items-center justify-center text-xs font-mono text-[#4A4A4A]"><Loader2 size={14} className="animate-spin mr-2" /> Cargando histórico…</div>;
    } else if (hist.status !== "ok") {
        body = (
            <div className="h-[160px] flex items-center justify-center text-center text-xs font-mono text-[#4A4A4A] px-4" data-testid="metric-chart-unavailable">
                {hist.status === "missing" ? "El histórico de métricas aún no está disponible en el servidor." : "No se pudo cargar el histórico de métricas."}
            </div>
        );
    } else if (!drawn.length) {
        body = (
            <div className="h-[160px] flex items-center justify-center text-center text-xs font-mono text-[#4A4A4A] px-4">
                {series.some((s) => s.on) ? "Sin datos de estas métricas para esta empresa." : "Toca una métrica para mostrarla."}
            </div>
        );
    } else {
        body = (
            <div ref={cardRef}>
                <div className="text-[10px] font-mono uppercase tracking-wide text-[#4A4A4A] mb-1" data-testid="metric-chart-axes">{axesNote(false)}</div>
                {renderChart(false)}
            </div>
        );
    }

    const footer = data && drawn.length > 0 && rows.length > 0 && (
        <div className="text-[10px] font-mono text-[#4A4A4A] mt-2 leading-snug" data-testid="metric-chart-note">
            Cifras publicadas{data.origin ? ` (${data.origin})` : ""}; los ratios con precio usan el cierre del mes de cada fecha.
            {projected && ` Tramo discontinuo: lo que proyecta el cálculo (tus inputs) a ${dateLong(projection.date)}.`}
            {trimmed && ` Desde ${rows[0].label}: la primera fecha con todas las métricas activas.`}
            {stale.map((x) => ` ${x.label}: sin cifras publicadas después de ${dateLong(x.date)}${ttm ? " (12 meses)" : ""}.`).join("")}
        </div>
    );

    const title = (
        <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
            <LineChartIcon size={14} className="shrink-0" />
            <span>Histórico de métricas · {chartId}</span>
            <HoverTip maxWidth={340} text={"Arrastra al gráfico cualquier fila de «Cifras de la empresa», «Ratios clásicos», «Crecimiento y rentabilidad futura» o los componentes de «Inputs y proyecciones» (en el móvil, «+ Métrica»). Los que el cálculo proyecta a +2 años (ingresos, FCF y sus CAGR) siguen con un tramo discontinuo hasta la fecha objetivo.\n\n• Hasta 6 métricas por gráfico (el precio incluido); son dos gráficos, cada uno con su propia selección.\n• Toca una métrica para ocultarla o mostrarla; arrástrala fuera del gráfico (o pulsa su ×) para quitarla, o al otro gráfico para moverla.\n• Anual: cada cierre fiscal. TTM: los 12 meses que acaban en cada trimestre.\n• Cada escala (precio, importes, múltiplos, %, acciones) tiene su eje, hasta 4 por gráfico, todas con el mismo periodo. «Relativa» lleva cada métrica de su mínimo a su máximo para comparar tendencias; los valores reales, en el recuadro.\n• Son cifras publicadas, no previsiones. Tu selección se recuerda al cambiar de empresa."}>
                <span className="cursor-help normal-case">ⓘ</span>
            </HoverTip>
        </div>
    );

    return (
        <div {...dropProps}
            className={`h-full border bg-white p-4 transition-shadow ${dragging || over ? "border-black shadow-[4px_4px_0_#111]" : "border-black"}`}
            data-testid={`metric-history-chart-${chartId}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                    {title}
                    <div className="font-serif text-xl">{ttm ? "Últimos 12 meses (TTM)" : "Evolución anual"}</div>
                </div>
                <button onClick={() => setExpanded(true)} disabled={!drawn.length || !rows.length}
                    className="text-[#4A4A4A] hover:text-black transition-colors shrink-0 disabled:opacity-30" title="Ampliar gráfico" data-testid="metric-chart-expand">
                    <Maximize2 size={16} />
                </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
                {modeToggle}
                {scaleToggle}
                {addSelect}
                {dragging && <span className="text-[11px] font-mono text-[#4A4A4A]">Suelta aquí para añadirla</span>}
            </div>
            {chips && <div className="mb-2">{chips}</div>}
            {body}
            {footer}
            {expanded && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-8" style={{ background: "rgba(17,17,17,0.55)" }}
                    data-testid="metric-chart-modal" onClick={() => setExpanded(false)}>
                    <div className="bg-white border border-black w-full max-w-5xl p-4 sm:p-6 flex flex-col max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-2 mb-2 shrink-0">
                            <div className="min-w-0">
                                {title}
                                <div className="font-serif text-lg sm:text-2xl">{ttm ? "Últimos 12 meses (TTM)" : "Evolución anual"}</div>
                            </div>
                            <button onClick={() => setExpanded(false)} className="text-[#4A4A4A] hover:text-black transition-colors shrink-0" title="Cerrar" data-testid="metric-chart-modal-close"><X size={22} /></button>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                            <div className="flex flex-wrap items-center gap-2">{modeToggle}{scaleToggle}</div>
                            <div className="flex items-center gap-2">
                                <ShareMenu size="md" title="Histórico de métricas" testidPrefix="metric-chart-share"
                                    createShare={async () => shareUpload(await getHtmlJpgBlob(exportRef.current), "jpg", `Historico metricas ${ticker}`)} />
                                <button onClick={downloadJpg} disabled={jpgBusy} className="text-[#4A4A4A] hover:text-black transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" title="Descargar en JPG" data-testid="metric-chart-download-jpg">
                                    {jpgBusy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                                </button>
                            </div>
                        </div>
                        <div ref={exportRef} className="bg-white p-1">
                            <div className="text-sm font-semibold mb-1">{ticker} · {ttm ? "últimos 12 meses (TTM)" : "evolución anual"}</div>
                            {chips && <div className="mb-2">{chips}</div>}
                            <div ref={bigRef}>
                                <div className="text-[10px] font-mono uppercase tracking-wide text-[#4A4A4A] mb-1">{axesNote(true)}</div>
                                {renderChart(true)}
                            </div>
                            {footer}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
